import type { ConnectorPropertiesSchema, FilteredDataSources } from "shared-ui";
import { getAccountingConnectors } from "shared-ui";
import type { FundingApplicationSchemas } from "api-types";

import { AuthCallbackPath } from "../api-types/connector-callbacks";
import {
  ACCOUNTING_CONNECTOR_SLUGS,
  ECOMMERCE_CONNECTOR_SLUGS,
  MARKETING_CONNECTOR_SLUGS,
  PAYMENT_CONNECTOR_SLUGS,
} from "../components/constants";
import type { FormattedFundingApplicationScreen } from "../queries/funding-application/queries";

/**
 * SPA specific inject next path for routes
 *
 * By default these come from the BE with a slug that maps to the SPA route.
 *
 * We need to update the references to this slug to refer to a specific page in the SPA instead of the root
 *
 * Ultimately we should be able to add a URL to the next_path instead of a django route slug but this will have
 * to do for now.
 * */
export const updateConnectorPaths = (connectors: ConnectorPropertiesSchema[], postPathRoute: AuthCallbackPath) => {
  return connectors.map((connector) => {
    return updateConnectorPath(connector, postPathRoute);
  });
};

const BASE_CONNECTOR_PATH = encodeURIComponent(AuthCallbackPath.FUNDING_APPLICATION.valueOf());

export const updateConnectorPath = (connector: ConnectorPropertiesSchema, postPathRoute: AuthCallbackPath) => {
  const encodedCallback = encodeURIComponent(postPathRoute.valueOf());
  if (
    connector.postPath &&
    connector.postPath.includes(BASE_CONNECTOR_PATH) &&
    !connector.postPath.endsWith(encodedCallback)
  ) {
    connector.postPath = connector.postPath.replace(BASE_CONNECTOR_PATH, encodedCallback);
  }
  if (connector.connectPath && connector.connectPath.includes(BASE_CONNECTOR_PATH)) {
    connector.connectPath = connector.connectPath.replace(BASE_CONNECTOR_PATH, encodedCallback);
  }
  return connector;
};

export const isEcommerceConnectorSelected = (selectedConnectorSlugs: string[] | undefined) => {
  return (
    !!selectedConnectorSlugs &&
    selectedConnectorSlugs?.some((slug) => ECOMMERCE_CONNECTOR_SLUGS.findIndex((connector) => connector === slug) >= 0)
  );
};

export const isPaymentsConnectorSelected = (selectedConnectorSlugs: string[] | undefined) => {
  return (
    !!selectedConnectorSlugs &&
    selectedConnectorSlugs?.some((slug) => PAYMENT_CONNECTOR_SLUGS.findIndex((connector) => connector === slug) >= 0)
  );
};

export const isMarketingConnectorSelected = (selectedConnectorSlugs: string[] | undefined) => {
  return (
    !!selectedConnectorSlugs &&
    selectedConnectorSlugs?.some((slug) => MARKETING_CONNECTOR_SLUGS.findIndex((connector) => connector === slug) >= 0)
  );
};

export const hasAccountingSourceSelected = (data: FormattedFundingApplicationScreen): boolean => {
  return (
    data.selectedConnectorSlugs.some(
      (slug) => ACCOUNTING_CONNECTOR_SLUGS.findIndex((connector) => connector === slug) >= 0
    ) ||
    Boolean(data.selectedUnsupportedAccounting.length) ||
    data.accountingDocumentsRequired ||
    false
  );
};

/**
 * Only "codat_file_uploads" and "codat_accounting" (platforms connected via codat) are excluded
 */
export const getFilteredAccountingConnectors = (connectors: FilteredDataSources) => {
  return getAccountingConnectors(connectors).filter(
    (connector) => connector.slug !== "codat_file_uploads" && connector.slug !== "codat_accounting"
  );
};

export const filterConnectorsByStageConfig = (
  connectors: ConnectorPropertiesSchema[],
  stageData?:
    | FundingApplicationSchemas.RevenueStageDataScreen
    | FundingApplicationSchemas.MarketingStageDataScreen
    | FundingApplicationSchemas.AccountingStageDataScreen
    | null
): ConnectorPropertiesSchema[] => {
  // If no stage config, allow all connectors
  if (!stageData) {
    return connectors;
  }

  // If allowedPlatformSelectionSlugs is null/undefined, no filter so allow all
  if (stageData.allowedPlatformSelectionSlugs === null || stageData.allowedPlatformSelectionSlugs === undefined) {
    return connectors;
  }

  // Filter down to allowed or required slugs
  return connectors.filter((connector) => {
    return (
      stageData.allowedPlatformSelectionSlugs?.includes(connector.slug) ||
      stageData.requiredConnectorSlugs?.includes(connector.slug)
    );
  });
};
